import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import LeaderboardCard from './LeaderboardCard'
import type { LeaderboardRow } from './LeaderboardCard'
import { useTopProducts } from '../hooks/use-top-products'
import { formatCurrency } from 'utils/executive.utils'

// Concentration thresholds — same shape as Top Procedures/Doctors, with the
// rows.length guard from Doctors so a small dental clinic with 1–2 SKUs
// doesn't get a trivial "100% concentrated" chip.
const TOP1_CONCENTRATION_PCT = 40
const TOP3_CONCENTRATION_PCT = 70
const MIN_ROWS_FOR_CONCENTRATION = 3

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function TopProductsCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useTopProducts()

  const title = t('EXECUTIVE.SALES.TOP_PRODUCTS.TITLE')

  const rows: LeaderboardRow[] =
    data?.items.map((i) => ({
      id: i.productId,
      name: i.productName,
      revenue: i.revenue,
      count: i.unitsSold,
      avgPerItem: i.avgPerUnit,
      percentShare: i.percentShare,
      variance: varianceOf(i.revenue, i.previousRevenue),
    })) ?? []

  const top1Share = rows[0]?.percentShare ?? 0
  const top3Share = rows.slice(0, 3).reduce((a, r) => a + (r.percentShare || 0), 0)
  const concentrated =
    rows.length >= MIN_ROWS_FOR_CONCENTRATION &&
    (top1Share >= TOP1_CONCENTRATION_PCT || top3Share >= TOP3_CONCENTRATION_PCT)

  const subtitle = data
    ? t(
        'EXECUTIVE.SALES.TOP_PRODUCTS.SUBTITLE',
        'Top {{count}} สินค้า • รวม {{total}}',
        {
          count: rows.length,
          total: formatCurrency(data.totalRevenue),
        }
      )
    : null

  const titleTooltip = t(
    'EXECUTIVE.SALES.TOP_PRODUCTS.TOOLTIP',
    '5 สินค้าที่ทำรายได้สูงสุดในช่วงวันที่เลือก เรียงตามยอดที่ลูกค้าชำระจริง (ไม่นับใบที่ถูกยกเลิก)\nหมายเหตุ: "สัดส่วน" = % ของ Top 5 / สินค้าที่ไม่อยู่ใน Top 5 จะไม่แสดง'
  )

  const notice = concentrated ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {top1Share >= TOP1_CONCENTRATION_PCT
            ? t(
                'EXECUTIVE.SALES.TOP_PRODUCTS.CONCENTRATION_TOP1',
                'พึ่งพาสินค้าเดียว: สินค้าอันดับ 1 = {{pct}}% ของ Top 5',
                { pct: top1Share.toFixed(0) }
              )
            : t(
                'EXECUTIVE.SALES.TOP_PRODUCTS.CONCENTRATION_TOP3',
                'รายได้สินค้ากระจุก: Top 3 = {{pct}}% ของ Top 5',
                { pct: top3Share.toFixed(0) }
              )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <LeaderboardCard
      title={title}
      titleTooltip={titleTooltip}
      subtitle={subtitle}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.SALES.TOP_PRODUCTS.EMPTY',
        'ไม่พบสินค้าในช่วงเวลาและสาขาที่เลือก'
      )}
      onClick={() => dispatch(pushDrill({ id: 'top-products', label: title }))}
      countColumnLabel={t('EXECUTIVE.SALES.COLUMN.UNITS')}
      avgColumnLabel={t('EXECUTIVE.SALES.COLUMN.AVG_PER_UNIT')}
      countUnit={t('EXECUTIVE.SALES.UNIT.UNIT')}
      rows={rows}
      totalRevenue={data?.totalRevenue ?? 0}
      loading={loading}
      error={error}
      onRetry={refetch}
      columnHeaders={{
        rank: t('EXECUTIVE.SALES.COLUMN.RANK'),
        name: t('EXECUTIVE.SALES.COLUMN.NAME'),
        revenue: t('EXECUTIVE.SALES.COLUMN.REVENUE'),
        share: t('EXECUTIVE.SALES.COLUMN.SHARE'),
      }}
    />
  )
}
